/**
 * URL Linkification Utility
 * Converts plain URLs in text to clickable HTML links
 */

/**
 * URL regex pattern - matches http, https, www, and common TLDs
 * Based on common URL patterns but simplified for safety
 */
const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.(com|org|net|edu|gov|io|co|uk|de|fr|it|es|nl|be|at|ch|se|no|dk|fi|pl|cz|hu|ro|gr|pt|ie|au|nz|ca|mx|br|ar|cl|co|pe|za|in|jp|kr|cn|sg|my|th|ph|id|vn|ae|sa|il|tr|ru|ua|by|kz|ge|am|az|jo|lb|kw|om|qa|bh|ye|iq|sy|ps|ma|dz|tn|ly|sd|so|et|ke|ug|tz|rw|gh|ng|cm|ci|sn|ml|bf|ne|td|cf|cg|cd|ga|gq|ao|zm|zw|bw|na|sz|ls|mw|mg|mu|sc|km|dj|er|bi|rw|ug|tz|ke|et|so|sd|ly|tn|dz|ma|ps|sy|iq|ye|bh|qa|om|kw|lb|jo|az|am|ge|kz|by|ua|ru|tr|il|sa|ae|vn|id|ph|th|my|sg|cn|kr|jp|in|za|pe|co|cl|ar|br|mx|ca|nz|au|ie|pt|gr|ro|hu|cz|pl|fi|dk|no|se|ch|at|be|nl|es|it|fr|de|uk|co|io|gov|edu|net|org|com)(\/[^\s<>"{}|\\^`[\]]*)?)/gi;

/**
 * Check if a URL is already inside an HTML tag (like <a> or <img>)
 */
const isInsideHtmlTag = (text: string, matchIndex: number): boolean => {
  const beforeMatch = text.substring(0, matchIndex);
  const lastTagOpen = beforeMatch.lastIndexOf('<');
  const lastTagClose = beforeMatch.lastIndexOf('>');
  
  // If there's an unclosed tag before this position, we're inside a tag
  return lastTagOpen > lastTagClose;
};

/**
 * Convert plain URLs in text to clickable HTML links
 * @param text - The text to linkify
 * @returns Text with URLs converted to HTML anchor tags
 */
export const linkifyUrls = (text: string): string => {
  if (!text) return '';
  
  // Don't process if text already contains HTML tags (to avoid double-processing)
  if (/<[^>]+>/.test(text)) {
    return text;
  }
  
  let result = text;
  const matches: Array<{ url: string; index: number }> = [];
  
  // Find all URL matches
  let match;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (!isInsideHtmlTag(text, match.index)) {
      matches.push({
        url: match[0],
        index: match.index
      });
    }
  }
  
  // Replace URLs from end to start to preserve indices
  for (let i = matches.length - 1; i >= 0; i--) {
    const { url, index } = matches[i];
    const href = url.startsWith('http') ? url : `https://${url}`;
    const link = `<a href="${href}" target="_blank" rel="noreferrer noopener nofollow" class="link">${url}</a>`;
    result = result.substring(0, index) + link + result.substring(index + url.length);
  }
  
  return result;
};

